import React from 'react';
import { Crown, AlertTriangle } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import UsageIndicator from './UsageIndicator';
import PremiumFeatureBadge from './PremiumFeatureBadge';

type FeatureType = 'contacts' | 'groups' | 'ai' | 'todos' | 'reminders';
type PlanType = 'free' | 'medium' | 'premium';

interface FeatureGateProps {
  feature: FeatureType;
  minimumTier?: PlanType;
  currentUsage?: number;
  limit?: number;
  showUpgradeButton?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * A component that conditionally renders its children based on the user's subscription level
 * and feature usage. It can show alternative content when the feature is not available.
 */
const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  minimumTier = 'free',
  currentUsage,
  limit,
  showUpgradeButton = true,
  children,
  className = ''
}) => {
  const { subscription, usageStats, setShowUpgradeModal } = useSubscription();
  
  // Get the current plan or default to 'free'
  const currentPlan = subscription?.planType || 'free';
  
  // Helper function to determine if the plan meets the minimum tier
  const planMeetsTier = (plan: PlanType, requiredTier: PlanType): boolean => {
    const tierLevels: Record<PlanType, number> = {
      'free': 0,
      'medium': 1,
      'premium': 2
    };
    
    return tierLevels[plan] >= tierLevels[requiredTier];
  };
  
  // Check if the user can access the feature based on their subscription
  const canAccessFeature = planMeetsTier(currentPlan, minimumTier);
  
  // Check if the user has hit usage limits
  const hasReachedLimit = () => {
    if (!currentUsage || !limit) return false;
    return currentUsage >= limit;
  };
  
  // If the user can access the feature and hasn't hit limits, show the children
  if (canAccessFeature && !hasReachedLimit()) {
    // If we're tracking usage, show a warning when reaching 80%
    if (currentUsage && limit && currentUsage / limit >= 0.8) {
      return (
        <div className={className}>
          <div className="mb-3">
            <UsageIndicator
              current={currentUsage}
              limit={limit}
              feature={`${feature.charAt(0).toUpperCase() + feature.slice(1)} Usage`}
            />
            {currentUsage / limit >= 0.9 && (
              <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={16} />
                  <span>You're about to reach your {feature} limit. Consider upgrading your plan.</span>
                </div>
                {showUpgradeButton && (
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="mt-2 px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 text-sm flex items-center gap-1"
                  >
                    <Crown size={14} />
                    <span>Upgrade Now</span>
                  </button>
                )}
              </div>
            )}
          </div>
          {children}
        </div>
      );
    }
    
    // If no usage concerns, just render children
    return <div className={className}>{children}</div>;
  }
  
  // If the user cannot access the feature due to their plan
  if (!canAccessFeature) {
    return (
      <div className={`p-4 border border-gray-200 rounded-lg bg-gray-50 ${className}`}>
        <div className="text-center py-8">
          <PremiumFeatureBadge 
            feature={minimumTier === 'premium' ? 'premium' : 'medium'}
            className="mx-auto mb-4"
          />
          
          <h3 className="text-lg font-semibold mb-2">
            This feature requires a {minimumTier} plan
          </h3>
          
          <p className="text-gray-600 mb-4">
            Upgrade your subscription to access {feature}.
          </p>
          
          {showUpgradeButton && (
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
            >
              <Crown size={16} />
              <span>Upgrade Now</span>
            </button>
          )}
        </div>
      </div>
    );
  }
  
  // If the user has reached their usage limit
  return (
    <div className={`p-4 border border-gray-200 rounded-lg bg-gray-50 ${className}`}>
      <div className="mb-3">
        <UsageIndicator
          current={currentUsage || 0}
          limit={limit || 0}
          feature={`${feature.charAt(0).toUpperCase() + feature.slice(1)} Usage`}
        />
      </div>
      
      <div className="text-center py-6">
        <AlertTriangle size={32} className="text-red-500 mx-auto mb-3" />
        
        <h3 className="text-lg font-semibold mb-2">
          You've reached your {feature} limit
        </h3>
        
        <p className="text-gray-600 mb-4">
          Upgrade your subscription to {currentPlan === 'free' ? 'medium' : 'premium'} for increased limits.
        </p>
        
        {showUpgradeButton && (
          <button
            onClick={() => setShowUpgradeModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
          >
            <Crown size={16} />
            <span>Upgrade Now</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default FeatureGate;